import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Loader2, ArrowRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fieldClass =
  "w-full bg-[color:var(--navy-950)] border border-[color:var(--border-subtle)] rounded-none px-4 py-3 text-[color:var(--text-primary)] placeholder:text-[color:var(--text-tertiary)] focus:outline-none focus:ring-1 focus:ring-[color:var(--gold-500)] focus:border-[color:var(--gold-500)] transition-colors";
const labelClass = "eyebrow block mb-2";

function FormShell({ children, onSubmit, submitting, ctaLabel, testIdPrefix }) {
  return (
    <form
      data-testid={`${testIdPrefix}-form`}
      onSubmit={onSubmit}
      className="bg-[color:var(--navy-900)] border border-[color:var(--border-subtle)] p-6 md:p-10 space-y-6"
    >
      {children}
      <button
        type="submit"
        disabled={submitting}
        data-testid={`${testIdPrefix}-submit`}
        className="btn-gold w-full md:w-auto inline-flex items-center justify-center gap-3 disabled:opacity-60 disabled:cursor-not-allowed"
      >
        {submitting ? <Loader2 className="animate-spin" size={18} /> : <ArrowRight size={16} />}
        {submitting ? "Sending…" : ctaLabel}
      </button>
    </form>
  );
}

export default function SignupSection() {
  const [tab, setTab] = useState("client");

  // Client form state
  const [client, setClient] = useState({ name: "", email: "", phone: "", message: "", consent: false });
  const [clientSubmitting, setClientSubmitting] = useState(false);

  // Careers form state
  const [careers, setCareers] = useState({
    name: "",
    email: "",
    phone: "",
    role_interest: "",
    linkedin_or_resume: "",
    message: "",
    consent: false,
  });
  const [careersSubmitting, setCareersSubmitting] = useState(false);

  const submitClient = async (e) => {
    e.preventDefault();
    if (!client.consent) {
      toast.error("Please confirm consent to proceed.");
      return;
    }
    setClientSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/signups/client`, client);
      toast.success(data.message || "Thank you. We'll be in touch.");
      setClient({ name: "", email: "", phone: "", message: "", consent: false });
    } catch (err) {
      const detail = err?.response?.data?.detail || "Something went wrong. Please try again.";
      toast.error(typeof detail === "string" ? detail : "Submission failed.");
    } finally {
      setClientSubmitting(false);
    }
  };

  const submitCareers = async (e) => {
    e.preventDefault();
    if (!careers.consent) {
      toast.error("Please confirm consent to proceed.");
      return;
    }
    setCareersSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/signups/careers`, careers);
      toast.success(data.message || "Application received.");
      setCareers({ name: "", email: "", phone: "", role_interest: "", linkedin_or_resume: "", message: "", consent: false });
    } catch (err) {
      const detail = err?.response?.data?.detail || "Something went wrong. Please try again.";
      toast.error(typeof detail === "string" ? detail : "Submission failed.");
    } finally {
      setCareersSubmitting(false);
    }
  };

  return (
    <section id="signup" data-testid="signup-section" className="relative px-6 md:px-12 lg:px-20 py-24 md:py-32 border-t border-[color:var(--border-subtle)]">
      <div className="max-w-5xl mx-auto">
        <div className="flex items-center gap-3 mb-6">
          <span className="w-10 h-px bg-[color:var(--gold-500)]" />
          <span className="eyebrow">Take the next step</span>
        </div>

        <h2 className="font-display font-black uppercase leading-[0.9] tracking-tighter text-5xl md:text-6xl">
          Choose your <span className="text-[color:var(--gold-500)]">path forward.</span>
        </h2>
        <p className="mt-6 max-w-2xl text-lg text-[color:var(--text-secondary)] leading-relaxed">
          Two doors. One mission. Get personal support — or join the team building the way forward.
        </p>

        <Tabs value={tab} onValueChange={setTab} className="mt-12">
          <TabsList
            data-testid="signup-tabs"
            className="w-full bg-transparent border border-[color:var(--border-subtle)] rounded-none grid grid-cols-2 h-auto p-0 gap-0"
          >
            <TabsTrigger
              value="client"
              data-testid="tab-client"
              className="rounded-none data-[state=active]:bg-[color:var(--gold-500)] data-[state=active]:text-[color:var(--navy-950)] data-[state=active]:shadow-none font-display uppercase tracking-[0.2em] text-sm py-4 transition-colors"
            >
              Client Intake
            </TabsTrigger>
            <TabsTrigger
              value="careers"
              data-testid="tab-careers"
              className="rounded-none data-[state=active]:bg-[color:var(--gold-500)] data-[state=active]:text-[color:var(--navy-950)] data-[state=active]:shadow-none font-display uppercase tracking-[0.2em] text-sm py-4 transition-colors"
            >
              Apply To Work With Us
            </TabsTrigger>
          </TabsList>

          <TabsContent value="client" className="mt-8">
            <FormShell
              onSubmit={submitClient}
              submitting={clientSubmitting}
              ctaLabel="Submit Intake"
              testIdPrefix="client"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label className={labelClass}>Full Name</Label>
                  <Input
                    data-testid="client-name"
                    required
                    value={client.name}
                    onChange={(e) => setClient({ ...client, name: e.target.value })}
                    placeholder="Your full name"
                    className={fieldClass}
                  />
                </div>
                <div>
                  <Label className={labelClass}>Email</Label>
                  <Input
                    data-testid="client-email"
                    type="email"
                    required
                    value={client.email}
                    onChange={(e) => setClient({ ...client, email: e.target.value })}
                    placeholder="you@example.com"
                    className={fieldClass}
                  />
                </div>
                <div className="md:col-span-2">
                  <Label className={labelClass}>Phone (optional)</Label>
                  <Input
                    data-testid="client-phone"
                    value={client.phone}
                    onChange={(e) => setClient({ ...client, phone: e.target.value })}
                    placeholder="(720) 555-0000"
                    className={fieldClass}
                  />
                </div>
                <div className="md:col-span-2">
                  <Label className={labelClass}>What brings you here?</Label>
                  <Textarea
                    data-testid="client-message"
                    rows={5}
                    value={client.message}
                    onChange={(e) => setClient({ ...client, message: e.target.value })}
                    placeholder="Share as much or as little as you're comfortable with. This stays confidential."
                    className={fieldClass}
                  />
                </div>
              </div>
              <label className="flex items-start gap-3 cursor-pointer">
                <Checkbox
                  data-testid="client-consent"
                  checked={client.consent}
                  onCheckedChange={(v) => setClient({ ...client, consent: !!v })}
                  className="mt-1 rounded-none border-[color:var(--border-gold)] data-[state=checked]:bg-[color:var(--gold-500)] data-[state=checked]:text-[color:var(--navy-950)]"
                />
                <span className="text-sm text-[color:var(--text-secondary)] leading-relaxed">
                  I consent to The Way Forward contacting me about mental health &amp; recovery
                  services. My information is kept confidential.
                </span>
              </label>
            </FormShell>
          </TabsContent>

          <TabsContent value="careers" className="mt-8">
            <FormShell
              onSubmit={submitCareers}
              submitting={careersSubmitting}
              ctaLabel="Submit Application"
              testIdPrefix="careers"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label className={labelClass}>Full Name</Label>
                  <Input
                    data-testid="careers-name"
                    required
                    value={careers.name}
                    onChange={(e) => setCareers({ ...careers, name: e.target.value })}
                    placeholder="Your full name"
                    className={fieldClass}
                  />
                </div>
                <div>
                  <Label className={labelClass}>Email</Label>
                  <Input
                    data-testid="careers-email"
                    type="email"
                    required
                    value={careers.email}
                    onChange={(e) => setCareers({ ...careers, email: e.target.value })}
                    placeholder="you@example.com"
                    className={fieldClass}
                  />
                </div>
                <div>
                  <Label className={labelClass}>Phone (optional)</Label>
                  <Input
                    data-testid="careers-phone"
                    value={careers.phone}
                    onChange={(e) => setCareers({ ...careers, phone: e.target.value })}
                    placeholder="(720) 555-0000"
                    className={fieldClass}
                  />
                </div>
                <div>
                  <Label className={labelClass}>Role of Interest</Label>
                  <Input
                    data-testid="careers-role"
                    value={careers.role_interest}
                    onChange={(e) => setCareers({ ...careers, role_interest: e.target.value })}
                    placeholder="Mentor · Coach · Operations · Other"
                    className={fieldClass}
                  />
                </div>
                <div className="md:col-span-2">
                  <Label className={labelClass}>LinkedIn or Resume URL</Label>
                  <Input
                    data-testid="careers-linkedin"
                    value={careers.linkedin_or_resume}
                    onChange={(e) => setCareers({ ...careers, linkedin_or_resume: e.target.value })}
                    placeholder="https://linkedin.com/in/yourname"
                    className={fieldClass}
                  />
                </div>
                <div className="md:col-span-2">
                  <Label className={labelClass}>Why The Way Forward?</Label>
                  <Textarea
                    data-testid="careers-message"
                    rows={5}
                    value={careers.message}
                    onChange={(e) => setCareers({ ...careers, message: e.target.value })}
                    placeholder="Tell us briefly about your background and why this mission matters to you."
                    className={fieldClass}
                  />
                </div>
              </div>
              <label className="flex items-start gap-3 cursor-pointer">
                <Checkbox
                  data-testid="careers-consent"
                  checked={careers.consent}
                  onCheckedChange={(v) => setCareers({ ...careers, consent: !!v })}
                  className="mt-1 rounded-none border-[color:var(--border-gold)] data-[state=checked]:bg-[color:var(--gold-500)] data-[state=checked]:text-[color:var(--navy-950)]"
                />
                <span className="text-sm text-[color:var(--text-secondary)] leading-relaxed">
                  I consent to The Way Forward contacting me about this application.
                </span>
              </label>
            </FormShell>
          </TabsContent>
        </Tabs>
      </div>
    </section>
  );
}
